import { provideHttpClient, withInterceptors } from '@angular/common/http';
import type { ApplicationConfig } from '@angular/core';
import {
  APP_INITIALIZER,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, Router } from '@angular/router';
import {
  APP_CONTEXT,
  APP_TYPE,
  createAuthInterceptor,
  createErrorInterceptor,
  DANGER_BTN,
  initializeLanguage,
  LanguageService,
  PersianNumpadService,
  provideSharedTranslations,
} from '@chaman/shared';
import { definePreset } from '@primeuix/themes';
import Material from '@primeuix/themes/material';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import appEn from '../assets/i18n/en.json';
import appFa from '../assets/i18n/fa.json';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';
import * as Sentry from '@sentry/angular';
// ─── Preset ──────────────────────────────────────────────────────────────────

const ChamanPatientPreset = definePreset(Material, {
  // Material's own primary semantic scale defaults to an unrelated emerald green
  // (#3BBFA1) that the old top-level `primary: { color }` shorthand only partly
  // covered -- every un-themed PrimeNG default (focus rings, highlights, list
  // selections, link color, form-field focus) was still inheriting Material's
  // green for every rung besides `.color`. This is the full fix.
  semantic: {
    primary: {
      50: 'var(--color-primary-50)',
      100: 'var(--color-primary-100)',
      200: 'var(--color-primary-200)',
      300: 'var(--color-primary-300)',
      400: 'var(--color-primary-400)',
      500: 'var(--color-primary-500)',
      600: 'var(--color-primary-600)',
      700: 'var(--color-primary-700)',
      800: 'var(--color-primary-800)',
      900: 'var(--color-primary-900)',
      950: 'var(--color-primary-950)',
    },
    colorScheme: {
      light: {
        primary: {
          color: 'var(--color-primary-600)',
          contrastColor: '#ffffff',
          hoverColor: 'var(--color-primary-700)',
          activeColor: 'var(--color-primary-800)',
        },
        highlight: {
          background: 'var(--color-primary-50)',
          focusBackground: 'var(--color-primary-100)',
          color: 'var(--color-primary-700)',
          focusColor: 'var(--color-primary-800)',
        },
        formField: {
          focusBorderColor: 'var(--color-primary-600)',
        },
      },
    },
  },
  components: {
    avatar: {
      css: `
        .p-avatar {
          background: linear-gradient(135deg, var(--color-primary-50) 0%, var(--color-primary-100) 100%);
          color: var(--color-primary-500);
          font-size: 1.5rem;
        }
      `,
    },
    floatlabel: {
      css: `
        .p-floatlabel {
          border: none;
          transition: background 0.2s ease, box-shadow 0.2s ease;
        }
        .p-floatlabel:hover {
          background: var(--bds-state-hover);
        }
      `,
    },
    tabs: {
      activeBar: {
        height: '3px',
        background: 'var(--color-primary-600)',
      },
      tabpanel: {
        padding: 'clamp({primitive.size.2}, 3vw, {primitive.size.4})',
      },
      tab: {
        activeBorderColor: 'var(--color-primary-600)',
        activeColor: 'var(--color-primary-600)',
      },
    },
    button: {
      colorScheme: {
        light: {
          root: {
            borderRadius: '{radius.xxsmall}',
            primary: {
              borderColor: 'transparent',
              hoverBorderColor: 'transparent',
              activeBorderColor: 'transparent',
              background: 'var(--color-primary-500)',
              hoverBackground: 'var(--color-primary-600)',
            },
            danger: {
              background: DANGER_BTN.color,
              borderColor: DANGER_BTN.color,
              activeBackground: DANGER_BTN.dark,
              hoverBackground: DANGER_BTN.dark,
            },
          },
          outlined: {
            primary: {
              color: 'var(--color-primary-500)',
              hoverBackground: 'var(--color-primary-50)',
            },
            danger: {
              borderColor: DANGER_BTN.color,
              color: DANGER_BTN.color,
            },
          },
        },
      },
      css: `
        .p-button-primary {
          box-shadow: var(--p-shadow-button-primary);
        }
        .p-button:disabled, .p-button[disabled] { opacity: 0.4 !important; }
        .p-button-success{
          background: var(--color-primary-600) !important;
          border: none !important;
        }
      `,
    },
    tag: {
      root: {
        borderRadius: '{radius.medium}',
      },
      css: `
        .p-tag {
          box-shadow: var(--p-shadow-tagShadow);
          padding: var(--p-spacing-1) var(--p-spacing-3);
          font-weight: normal;
        }
        .p-tag-secondary{
          background: #ffffff66;
          border: 0;
        }
      `,
    },
    checkbox: {
      root: {
        borderRadius: '{primitive.radius.xxsmall}',
      },
      css: `
        .p-checkbox {
          height: 18px;
          width: 18px;
        }
        .p-checkbox-box {
          height: 18px;
          width: 18px;
          border-width: 2px;
        }
      `,
    },
    selectbutton: {
      css: `
        .pill-select {
          display: flex;
          gap: var(--p-spacing-3);
          background: transparent;
          border: none;
          padding: 0;
          width: 100%;
        }
        .p-selectbutton{
          width: 100%;
          gap: 1rem;
        }
        .pill-select .p-selectbutton-option {
          border-radius: 999px;
          border: 1.5px solid var(--color-primary-200);
          background: transparent;
          color: var(--p-color-gray-600);
          padding: 6px 20px;
          font-size: 0.875rem;
          font-weight: 500;
          flex: 1;
          justify-content: center;
          transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
        }
        .pill-select .p-selectbutton-option:not(.p-selectbutton-option-selected):hover {
          border-color: var(--color-primary-400);
          background: color-mix(in oklab, var(--color-primary-500) 8%, transparent);
          color: var(--color-primary-600);
        }
        .pill-select .p-selectbutton-option-selected {
          border-color: var(--color-primary-500);
          background: color-mix(in oklab, var(--color-primary-500) 15%, transparent);
          color: var(--color-primary-700);
          font-weight: 600;
          box-shadow: 0 0 0 1px var(--color-primary-300);
        }
        .p-togglebutton{
          min-height: var(--p-size-7) !important;
          padding: 0 !important;
          border-radius: var(--p-radius-xx-small) !important;
          color:  var(--p-black) !important;
          font-weight: 500 !important;
          border-width: 1px !important;
        }
        .p-selectbutton.ng-invalid.ng-dirty{
          outline: none !important
        }
        .p-togglebutton-content:hover{
          background: color-mix(in oklab,var(--p-white)30%,transparent) !important;
          border-color: color-mix(in oklab,var(--color-primary-500)50%,transparent)!important;
        }
        .p-togglebutton-checked .p-togglebutton-content{
          border: 2px solid var(--color-primary-500) !important;
          border-radius: var(--p-radius-xx-small) !important;
          background: var(--color-primary-alpha-10) !important;
        }
      `,
    },
    select: {
      colorScheme: {
        light: {
          root: {
            filledBackground: 'transparent',
            filledHoverBackground: 'transparent',
            filledFocusBackground: 'transparent',
          },
        },
      },
      css: `
        .p-select.p-variant-filled {
          border: 0 !important;
          border-bottom: 1px solid var(--color-surface-300) !important;
          border-radius: 0 !important;
        }
        .p-select.p-variant-filled:not(.p-disabled):hover {
          border-bottom-color: var(--color-primary) !important;
        }
        .p-select.p-variant-filled:not(.p-disabled).p-focus {
          outline: none !important;
        }
      `,
    },
    inputtext: {
      colorScheme: {
        light: {
          root: {
            borderColor: '{primitive.color.gray.300}',
            hoverBorderColor: 'var(--color-primary-600)',
            focusBorderColor: 'var(--color-primary-600)',
            borderRadius: '{radius.xsmall}',
            focusRing: {
              color: 'var(--color-primary-600)',
            },
          },
        },
      },
    },
    textarea: {
      colorScheme: {
        light: {
          root: {
            borderColor: '{primitive.color.gray.300}',
            hoverBorderColor: 'var(--color-primary-600)',
            focusBorderColor: 'var(--color-primary-600)',
            borderRadius: '{radius.xsmall}',
            focusRing: {
              color: 'var(--color-primary-600)',
            },
          },
        },
      },
    },
    inputgroup: {
      addon: {
        borderColor: '{primitive.color.gray.300}',
      },
    },
    radiobutton: {
      css: `
        .p-radiobutton {
          height: 20px;
          width: 20px;
        }
        .p-radiobutton-box {
          height: 20px;
          width: 20px;
          border-width: 2px;
        }
      `,
    },
    menu: {
      css: `
        .p-menu-item-link {
          padding: var(--p-spacing-2);
          font-size: var(--p-font-size-sm);
          display: flex;
          justify-content: end;
          flex-direction: row-reverse;
        }
        .p-menu-item-icon {
          font-size: var(--p-font-size-sm);
        }
        .p-menu .p-menu-overlay {
          margin-top: var(--p-spacing-offset);
        }
      `,
    },
    drawer: {
      css: `
        .mobile-filter-sheet {
          height: auto !important;
          max-height: 85dvh !important;
          border-radius: var(--p-radius-small) var(--p-radius-small) 0 0 !important;
          box-shadow: var(--bds-elevation-2) !important;
          overflow: hidden;
          background: var(--p-glass-card-bg) !important;
          backdrop-filter: blur(var(--p-glass-blur)) !important;
          -webkit-backdrop-filter: blur(var(--p-glass-blur)) !important;
          border: none !important;
        }
        .mobile-filter-sheet .p-drawer-header {
          padding: var(--p-spacing-3) var(--p-spacing-4) var(--p-spacing-4);
          border-bottom: 1px solid var(--p-surface-200);
          background: transparent !important;
        }
        .mobile-filter-sheet .p-drawer-content {
          padding: 0 !important;
          background: transparent !important;
          overflow-y: auto;
        }
        .mobile-filter-sheet .p-drawer-close-button { color: var(--p-surface-500); }
        .mobile-filter-sheet .p-drawer-close-button:hover { background: var(--p-surface-100); color: var(--p-surface-700); }
      `,
    },
    card: {
      root: {
        borderRadius: '{primitive.radius.medium}',
        shadow: '{primitive.shadow.card}',
      },
      body: {
        padding: 'clamp({primitive.size.2}, 3vw, {primitive.size.4})',
      },
    },
    scrollpanel: {
      colorScheme: {
        light: {
          bar: {
            background: 'var(--color-primary-600)',
            size: '5px',
          },
        },
      },
    },
    toast: {
      root: {
        width: 'clamp(280px, calc(100vw - 2.4rem), 25rem)',
      },
    },
  },
  primitive: {
    white: '#FFFFFF',
    // Bugloos DS trial: glass, gradient and shadow primitives alias the DS skin tokens
    // (data-skin on <html>), so the Soft skin's flat tint and diffuse elevation apply.
    glass: {
      bg: 'var(--bds-skin-surface)',
      cardBg: 'var(--bds-skin-surface)',
      inputBg: 'var(--bds-skin-surface-sunken)',
      border: 'var(--bds-border-width) solid var(--bds-skin-border)',
      blur: '0px',
      radius: 'var(--bds-radius-sm)',
      shadow: 'var(--bds-elevation-1)',
      overlay: 'var(--bds-color-scrim)',
    },
    gradient: {
      bg: 'var(--bds-skin-bg)',
      btnAccent: 'var(--bds-color-primary)',
    },
    color: {
      deepIndigo: 'var(--bds-brand-800)',
      indigo600: 'var(--bds-color-primary)',
      indigoLighter: 'var(--bds-brand-50)',
      indigoLight: 'var(--bds-brand-100)',
      indigoDeepDark: 'var(--bds-brand-900)',
      black: '#000',
      white: '#fff',
      red: {
        500: 'oklch(63.7% 0.237 25.331)',
      },
      yellow: {
        500: 'oklch(79.5% 0.184 86.047)',
      },
      green: {
        50: 'oklch(98.2% 0.018 155.826)',
        600: 'oklch(62.7% 0.194 149.214)',
        700: 'oklch(52.7% 0.154 150.069)',
      },
      gray: {
        100: 'oklch(96.7% 0.003 264.542)',
        200: 'oklch(92.8% 0.006 264.531)',
        300: 'oklch(87.2% 0.01 258.338)',
        400: 'oklch(70.7% 0.022 261.325)',
        500: 'oklch(55.1% 0.027 264.364)',
        600: 'oklch(44.6% 0.03 256.802)',
        700: 'oklch(37.3% 0.034 259.733)',
        800: 'oklch(27.8% 0.033 256.848)',
        900: 'oklch(21% 0.034 264.665)',
      },
    },
    shadow: {
      primary: 'var(--bds-elevation-2)',
      glow: 'var(--bds-elevation-1)',
      buttonPrimary: 'none',
      buttonAccent: 'none',
      inputSurface: 'none',
      card: 'var(--bds-elevation-1)',
    },
    size: {
      1: '8px',
      2: '16px',
      3: '24px',
      4: '32px',
      5: '40px',
      6: '48px',
      7: '56px',
      8: '64px',
      9: '72px',
      10: '80px',
      12: '96px',
      14: '112px',
      16: '128px',
      20: '160px',
      24: '192px',
      28: '224px',
      32: '256px',
      40: '320px',
      48: '384px',
      56: '448px',
      64: '512px',
    },
    // Bugloos DS trial: steps 0, 5 and 7 were missing, so any rule written against
    // `--p-spacing-5` (e.g. the shared dialog header padding in base.css) was invalid at
    // computed-value time and collapsed to 0. Doctor fixed its own scale the same way.
    spacing: {
      0: '0px',
      1: '4px',
      2: '8px',
      3: '12px',
      4: '16px',
      5: '20px',
      6: '24px',
      7: '28px',
      8: '32px',
      12: '40px',
      13: '60px',
      14: '90px',
      20: '80px',
      offset: '6px',
    },
    fontSize: {
      sm: '0.875rem',
      base: '1rem',
      md: '1.25rem',
      lg: '1.5rem',
      xl: '2rem',
      '2xl': '2.25rem',
    },
    fontWeight: {
      thin: 100,
      extralight: 200,
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
      black: 900,
    },
    radius: {
      xxsmall: '4px',
      xsmall: '8px',
      small: '16px',
      medium: '20px',
      large: '32px',
    },
    dangerBtn: {
      color: DANGER_BTN.color,
      hoverBg: DANGER_BTN.hoverBg,
    },
    sidebar: {
      width: 'var(--p-size-32)',
    },
    collapsedSidebar: {
      width: 'var(--p-spacing-20)',
    },
    animation: {
      duration: '0.8s',
    },
    table: {
      borderBottom: '1px solid var(--p-gray-200)',
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: APP_CONTEXT, useValue: APP_TYPE.PATIENT },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes),
    provideHttpClient(
      withInterceptors([
        createAuthInterceptor(),
        createErrorInterceptor({
          production: environment.production,
          silentErrorUrls: ['/auth/patient/register/verify'],
        }),
      ])
    ),
    provideAnimationsAsync(),
    MessageService,
    providePrimeNG({
      ripple: true,
      theme: {
        preset: ChamanPatientPreset,
        options: {
          prefix: 'p',
          darkModeSelector: '.dark',
        },
      },
      pt: {
        inputgroupaddon: {
          root: {
            style: {
              boxShadow: 'none',
            },
          },
        },
      },
    }),
    provideSharedTranslations({
      en: appEn,
      fa: appFa,
    }),
    provideAppInitializer(() => {
      const initializerFn = initializeLanguage(inject(LanguageService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      inject(PersianNumpadService).init();
    }),
  ],
};
